import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';
import SortIcon from '../../../../../Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import OfficeIcon from '../../../../../../assets/Svg/OfficeIcon';
import CaretIcon from '../../../../../../assets/Svg/CaretIcon';
import { SORT_DIRECTIONS, SortConfig } from '../../../../../../utils/constants/commonConstants';
import { formatHalfYearLabel } from '../../../../../../utils/dateUtils';
import type { MsmeDelayGraph, MsmeSupplierPlotValue } from '../../../../../../store/dashboard/compliancesBefe/types';
import {
    ARIA_SORT_BY_DIRECTION,
    MSME_AMOUNT_SORT_FIELD,
    MSME_DRAWER_COLUMNS,
    MSME_DRAWER_TITLE,
    MSME_DRAWER_WIDTH,
    MSME_EXPAND_SUPPLIERS_LABEL,
} from './constants';
import { formatMsmeDrawerAmount } from './msmeFormatters';
import styles from './MsmeDelayDrawer.module.scss';

interface MsmeDelayDrawerProps {
    graphData: MsmeDelayGraph;
    /** Supplier PAN to highlight and scroll to. */
    highlightPan?: string;
    onClose: () => void;
}

const byAmount = (direction: SortConfig['direction']) => (a: { value: number | null }, b: { value: number | null }) => {
    const sign = direction === SORT_DIRECTIONS.ASC ? 1 : -1;
    return sign * ((a.value ?? -Infinity) - (b.value ?? -Infinity));
};

/**
 * Bespoke table rather than DataTable: the latest period expands into supplier sub-rows, which
 * DataTable cannot model, and DataTable is wired to the investor store.
 */
const MsmeDelayDrawer = ({ graphData, highlightPan, onClose }: MsmeDelayDrawerProps) => {
    const isMobile = useIsMobile();
    const [isLatestExpanded, setIsLatestExpanded] = useState(true);
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>();
    const highlightedRowRef = useRef<HTMLTableRowElement>(null);

    const periods = graphData?.plots?.[0]?.plotValues ?? [];
    const suppliers = graphData?.plots?.[1]?.plotValues ?? [];
    const latestMonthKey = periods.reduce((latest, period) => (period.xValue > latest ? period.xValue : latest), '');
    const highlightKey = highlightPan?.toUpperCase();

    const sortedPeriods = sortConfig
        ? [...periods].sort(byAmount(sortConfig.direction))
        : [...periods].sort((a, b) => b.xValue.localeCompare(a.xValue));
    const keyedSuppliers = withSupplierRowKeys(suppliers);
    const sortedSuppliers = sortConfig
        ? [...keyedSuppliers].sort((a, b) => byAmount(sortConfig.direction)(a.supplier, b.supplier))
        : keyedSuppliers;

    useEffect(() => {
        highlightedRowRef.current?.scrollIntoView?.({ block: 'nearest' });
    }, []);

    const toggleAmountSort = () =>
        setSortConfig((current) => ({
            field: MSME_AMOUNT_SORT_FIELD,
            direction: current?.direction === SORT_DIRECTIONS.ASC ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC,
        }));

    return createPortal(
        <SideDrawer
            heading={<span className={styles.Title}>{MSME_DRAWER_TITLE}</span>}
            ariaLabel={MSME_DRAWER_TITLE}
            showHeading
            allowClose
            headerVariant="tight"
            width={isMobile ? '100%' : MSME_DRAWER_WIDTH}
            onClose={onClose}
        >
            <div className={styles.Body}>
                <div className={styles.TableFrame}>
                    <table className={styles.Table}>
                        <colgroup>
                            <col className={styles.ChevronColumn} />
                            <col />
                            <col className={styles.PanColumn} />
                            <col className={styles.AmountColumn} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th />
                                <th>{MSME_DRAWER_COLUMNS.FINANCIAL_YEAR}</th>
                                <th className={styles.Right}>{MSME_DRAWER_COLUMNS.PAN}</th>
                                <th
                                    className={styles.Right}
                                    aria-sort={sortConfig?.direction ? ARIA_SORT_BY_DIRECTION[sortConfig.direction] : 'none'}
                                >
                                    <span className={styles.SortHeader}>
                                        {MSME_DRAWER_COLUMNS.AMOUNT}
                                        <SortIcon
                                            field={MSME_AMOUNT_SORT_FIELD}
                                            sortConfig={sortConfig}
                                            onSort={toggleAmountSort}
                                            alwaysVisible
                                            className={styles.SortButton}
                                        />
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedPeriods.map((period) => {
                                const isExpandable = period.xValue === latestMonthKey && suppliers.length > 0;
                                const isExpanded = isExpandable && isLatestExpanded;
                                const periodLabel = formatHalfYearLabel(period.xValue);
                                return [
                                    <tr
                                        key={period.xValue}
                                        data-testid="msme-period-row"
                                        className={clsx({ [styles.ExpandableRow]: isExpandable })}
                                        onClick={isExpandable ? () => setIsLatestExpanded((expanded) => !expanded) : undefined}
                                    >
                                        <td className={styles.ChevronCell}>
                                            {isExpandable && (
                                                <button
                                                    type="button"
                                                    className={clsx(styles.ExpandButton, { [styles.Expanded]: isExpanded })}
                                                    aria-expanded={isExpanded}
                                                    aria-label={`${MSME_EXPAND_SUPPLIERS_LABEL} ${periodLabel}`}
                                                >
                                                    <CaretIcon colour="currentColor" width="14" height="14" />
                                                </button>
                                            )}
                                        </td>
                                        <td>{periodLabel}</td>
                                        <td className={styles.Right} />
                                        <td className={styles.Right}>{formatMsmeDrawerAmount(period.value)}</td>
                                    </tr>,
                                    ...(isExpanded
                                        ? sortedSuppliers.map(({ supplier, key }) => {
                                              const isHighlighted = !!highlightKey && supplier.id?.toUpperCase() === highlightKey;
                                              return (
                                                  <tr
                                                      key={key}
                                                      ref={isHighlighted ? highlightedRowRef : undefined}
                                                      className={clsx(styles.SupplierRow, { [styles.Highlighted]: isHighlighted })}
                                                      data-testid="msme-supplier-row"
                                                  >
                                                      <td />
                                                      <td className={styles.SupplierNameCell}>
                                                          <span className={styles.SupplierName}>
                                                              <OfficeIcon width={16} height={16} />
                                                              <span className={styles.SupplierNameText} title={supplier.name ?? undefined}>
                                                                  {supplier.name}
                                                              </span>
                                                          </span>
                                                      </td>
                                                      <td className={styles.Right}>{supplier.id}</td>
                                                      <td className={styles.Right}>{formatMsmeDrawerAmount(supplier.value)}</td>
                                                  </tr>
                                              );
                                          })
                                        : []),
                                ];
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </SideDrawer>,
        document.body,
    );
};

/**
 * Pairs each supplier with a row key from PAN + name, assigned on the unsorted list so a re-sort
 * keeps every row (and the highlighted-row ref) mounted. Only exact duplicates get an ordinal suffix.
 */
function withSupplierRowKeys(suppliers: MsmeSupplierPlotValue[]): { supplier: MsmeSupplierPlotValue; key: string }[] {
    const occurrences = new Map<string, number>();
    return suppliers.map((supplier) => {
        const baseKey = `${supplier.id ?? ''}|${supplier.name ?? ''}`;
        const seen = occurrences.get(baseKey) ?? 0;
        occurrences.set(baseKey, seen + 1);
        return { supplier, key: seen ? `${baseKey}#${seen}` : baseKey };
    });
}

export default MsmeDelayDrawer;
